import { ApplicantProfile } from 'api/applicantProfile';
import { ListingApplicationType } from 'api/ats';
import { FilestackImageData } from 'api/shared';
import { del, getJson, postJson, putJson } from 'utils/http';

export type SavedSearchIndex = 'ACTIONS' | 'MAIN';
export type SavedSearchUnsaved = {
  text: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any>;
  location: SearchLocation;
  radius: number | null | undefined;
  index: SavedSearchIndex;
};
export type SavedSearch = SavedSearchUnsaved & {
  id: string;
  created: string;
};
export type UserOrgMini = {
  id: string;
  name: string;
  searchApiKey: string;
  addressCountry: string;
  canPostGradProgram: boolean;
  canPostVolops: boolean;
  canBuyCredits: boolean;
  // @ts-expect-error ts-migrate(2300) FIXME: Duplicate identifier 'hasGradProgram'.
  hasGradProgram: boolean;
  hasAts: boolean;
  gradProgramName: string | null | undefined;
  isApproved: boolean;
  // @ts-expect-error ts-migrate(2300) FIXME: Duplicate identifier 'hasGradProgram'.
  hasGradProgram: boolean;
  isAllowedToPost: boolean;
  isFrozen: boolean;
  overrideToMainDashboard: boolean;
};
export type UserGroupMini = {
  id: string;
  name: string;
  searchApiKey: string;
  isApproved: boolean | null | undefined;
  isFrozen: boolean;
};
export type ABExperiments = Array<{
  id: string;
  created: string;
  bucket: string;
}>;
export type CurrentUser = {
  id: string | null | undefined;
  email: string | null | undefined;
  emailVerified: boolean | null | undefined;
  hasPassword: boolean;
  name: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  locale: UserLocale;
  profile?: {
    description: string;
    links: Array<string>;
    image: FilestackImageData | null | undefined;
  };
  isRecruitable: boolean | null | undefined;
  created: string;
  savedSearches: Array<SavedSearch>;
  orgs: Array<UserOrgMini>;
  groups: Array<UserGroupMini>;
  hasEvents: boolean;
  permissions: Array<'user' | 'staff' | 'superStaff' | 'orgAdmin'>;
  searchLocation: SearchLocation;
  location:
    | {
        text: string;
        stateCode: string | null | undefined;
        countryCode: string;
        latitude: string;
        longitude: string;
      }
    | null
    | undefined;
  timezone: string | null | undefined;
  savedListings: Array<{
    id: string;
    type: string;
  }>;
  extraData: {
    staffSearchApiKey?: string;
  };
  listingApplications: Array<{
    id: string;
    type: ListingApplicationType;
  }>;
  resumeId: string | null | undefined;
  abExperiments: ABExperiments;
  showAcceptCookies: boolean;
  hasNotificationPreferences: boolean;
  showNotifPrefModal: boolean;
  isEU: boolean;
  isUS: boolean;
  idealistSessionId: string;
  canExportData: boolean;
  authProviders: Array<'google_oauth2' | 'facebook'>;
  contactedGradPrograms: Array<string>;
  isLoaded: boolean;
  applicantProfile: ApplicantProfile;
  applicantProfileIsLoaded: boolean;
  applicantProfileIsFetching: boolean;
  hasNotifications: boolean;
  notifications?: Array<Notification>;
  salarySurvey: {
    lastSubmitted: string | null | undefined;
  };
};
export type ProfileInfo = {
  description: string;
  links: Array<string>;
  image: FilestackImageData | null | undefined;
};
export type AuthProvider = {
  name: string;
  code: string;
  value: 'google' | 'facebook';
};
export const fetchCurrentUser = (): Promise<CurrentUser> =>
  getJson(`/data/users/me`).then(({ data }) => data);
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateSettings = (settings: Record<string, any>) =>
  putJson('/data/userdashboard/general-settings', settings);
export const updateProfile = (profileInfo: ProfileInfo): Promise<CurrentUser> =>
  putJson('/data/userdashboard/profile', profileInfo).then(({ user }) => user);
export const changePassword = (
  oldPassword: string | null | undefined,
  password: string
) =>
  putJson(`/data/userdashboard/password`, {
    oldPassword,
    password,
  });
export const updateMissingInfo = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userInfo: Record<string, any>
): Promise<CurrentUser> =>
  postJson('/data/userdashboard/missing-info', userInfo).then(
    ({ user }) => user
  );
export const canDeleteAccount = () => getJson('/data/users/me/can-delete');
export const deleteAccount = () => del('/data/users/me/delete');
export const unlinkOAuthAccount = (authProvider: string) =>
  del(`/data/userdashboard/linked-accounts/${authProvider}`);
export const saveListing = (id: string, type: string) =>
  postJson(`/data/saved-listings`, {
    id,
    type,
  });
export const unsaveListing = (id: string, type: string) =>
  del(`/data/saved-listings/${type}/${id}`);
export const saveSearch = ({
  text,
  filters,
  location,
  index,
  radius,
}: SavedSearchUnsaved) =>
  postJson('/data/saved-searches', {
    text,
    filters,
    radius,
    location,
    index,
  });
export const unsaveSearch = (searchId: string) =>
  del(`/data/saved-searches/${searchId}`);
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const signUp = (userInfo: Record<string, any>): Promise<CurrentUser> =>
  postJson('/data/website/signup', userInfo).then(({ user }) => user);
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logIn = (loginInfo: Record<string, any>): Promise<CurrentUser> =>
  postJson('/data/website/login', loginInfo).then(({ user }) => user);
export const forgotPassword = (email: string): Promise<void> =>
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  postJson('/data/website/forgot-password', {
    email,
  });
export const resetPassword = ({
  password,
  token,
}: {
  password: string;
  token: string;
}): Promise<CurrentUser> =>
  postJson(`/data/website/reset-password/${token}`, {
    password,
  }).then(({ user }) => user);
export const submitContactInfo = (
  gradProgramId: string,
  body: {
    email: string;
    firstName: string;
    lastName: string;
    interestedInDegreeProgram?: string;
    expectedEnrollmentDate?: string;
    notes?: string;
  }
): Promise<void> =>
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'Promise<unknown>' is not assignable to type ... Remove this comment to see the full error message
  postJson(`/data/website/grad-programs/${gradProgramId}/share-contact`, body);
export const setABExperimentBucket = (
  experimentId: string,
  bucket?: string
): Promise<ABExperiments> =>
  postJson('/data/ab/assignments', {
    id: experimentId,
    bucket,
  }).then(({ abExperiments }) => abExperiments);
